/**
 * The layout rules behind a windowed file list (kitecorp/transferify-ui#2942): which rows are drawn, and
 * how much space stands in for the rows that are not.
 *
 * A transfer may hold 10,000 files. Drawing a row for each stalls the page, most of all on the upload
 * card, which re-renders ten times a second. A windowed list draws only the rows in view plus a few on
 * either side, and gives the drawn rows margins so the list keeps the full height, scrollbar and
 * position it would have with every row drawn.
 *
 * The rows stay in normal flow rather than being absolutely positioned, so every list keeps its own
 * container, flex gap, borders and hover styles exactly as before. The margins go on the drawn rows
 * themselves, not on spacer elements, because a spacer inside a flex container with a `gap` would add a
 * gap of its own.
 *
 * {@link ./useWindowedList} is the React adapter; this module holds the arithmetic so it can be tested
 * without a browser.
 */

/**
 * Lists up to this many rows draw every row, exactly as they did before windowing existed. Everyday
 * transfers stay on that path, where nothing about the markup changed; only a list long enough to cost
 * real rendering time is windowed.
 */
export const WINDOWED_LIST_THRESHOLD = 50;

/** Whether a list of `count` rows draws only the rows near its visible part. */
export function isWindowed(count: number): boolean {
	return count > WINDOWED_LIST_THRESHOLD;
}

/**
 * The drawn rows, plus the row holding keyboard focus when it has scrolled out of the window.
 *
 * Unmounting the focused row would drop focus to the page body, so a keyboard user who scrolled with
 * the wheel would lose their place. Keeping that one row drawn keeps focus where it was.
 */
export function withRetainedRow(indexes: readonly number[], retained: number | null, count: number): number[] {
	if (retained === null || retained >= count || indexes.includes(retained)) {
		return [...indexes];
	}
	return [...indexes, retained].sort((a, b) => a - b);
}

/** How many estimated rows a list draws while its scroller measures no height. */
const ROWS_WITHOUT_A_HEIGHT = 20;

/**
 * The height of the view to window against: the scroller's measured height, or twenty estimated rows
 * while it measures nothing.
 *
 * Most of these lists take their height from their own rows (a `max-height`, or a flex item that
 * shrinks to fit). Drawn with no rows they measure 0, and a 0-height view draws no rows, so the list
 * would stay empty for good. Twenty rows is enough to fill any of them; the real height is measured as
 * soon as they are drawn.
 */
export function viewHeight(measuredHeight: number, estimateRowHeight: number): number {
	return measuredHeight > 0 ? measuredHeight : estimateRowHeight * ROWS_WITHOUT_A_HEIGHT;
}

/** A drawn row's place in the whole list, in the virtualizer's coordinates. */
export interface PlacedRow {
	index: number;
	/** Distance from the scroller's content top to the row's top. */
	start: number;
	end: number;
}

/** Extra space before and after one drawn row, standing in for rows that are not drawn. */
export interface RowSpacing {
	marginTop: number;
	marginBottom: number;
}

/**
 * The margins that put each drawn row where it would sit with every row drawn.
 *
 * `rows` are the drawn rows in index order, `listStart` is where the list's first row starts in the
 * same coordinates, `totalSize` is the whole list's height from that start, and `gap` is the container's
 * CSS gap between rows (0 for a block container). Between two drawn rows the container already adds
 * one `gap`, so only the height of the rows skipped between them becomes margin.
 */
export function rowSpacing(rows: readonly PlacedRow[], listStart: number, totalSize: number, gap: number): RowSpacing[] {
	return rows.map((row, position) => {
		const previous = position === 0 ? null : rows[position - 1];
		const marginTop = previous === null ? row.start - listStart : row.start - previous.end - gap;
		const marginBottom = position === rows.length - 1 ? totalSize - (row.end - listStart) : 0;
		return {marginTop, marginBottom};
	});
}

/** Measurements, in viewport pixels, that locate a list inside the element that scrolls it. */
export interface ScrollerGeometry {
	/** `getBoundingClientRect().top` of the scrolling element. */
	scrollerTop: number;
	/** Its top border width (`clientTop`); the scrolled content starts inside it. */
	scrollerBorderTop: number;
	scrollTop: number;
	/** `getBoundingClientRect().top` of the first drawn row. */
	firstRowTop: number;
	/** How far that row sits below the list's start: the top margin {@link rowSpacing} gave it. */
	firstRowOffset: number;
}

/**
 * Where the list's first row starts inside the scroller's scrollable content: the scroller's own
 * padding, a sticky header, or everything above the list in a page that scrolls as a whole. The
 * virtualizer needs it to tell which rows are in view.
 *
 * Measured from a drawn row rather than kept as a constant, because the same hook serves lists that
 * scroll themselves and one that scrolls with the page around it, where the content above can change.
 */
export function listStartInScroller(geometry: ScrollerGeometry): number {
	const contentOrigin = geometry.scrollerTop + geometry.scrollerBorderTop - geometry.scrollTop;
	return geometry.firstRowTop - geometry.firstRowOffset - contentOrigin;
}
